import React from 'react';

/**
 * MessageStatus Component
 * 
 * Renders WhatsApp-style message status indicators (ticks) for sent messages
 * Supports different statuses: sending, failed, sent, delivered, read, offline
 * Includes special handling for admin conversations
 * 
 * @param {Object} msg - Message object
 * @param {Object} user - Current user object
 * @param {Object} conversation - Current conversation object
 * @param {boolean} isDarkMode - Dark mode state
 * @returns {JSX.Element|null} Status indicator or null if not from current user
 */
const MessageStatus = ({ msg, user, conversation, isDarkMode }) => {
  // Check if the message is from the current user - using same logic as MessageBubble
  const isFromCurrentUser = user && (
    // Check if message is from current user by email
    (msg.from && user.email && msg.from.toLowerCase() === user.email.toLowerCase()) ||
    // Check if message is from current user by UID
    (msg.from && user.uid && msg.from === user.uid) ||
    // Check if message is from current user by fromUid field
    (msg.fromUid && user.uid && msg.fromUid === user.uid) ||
    (msg.fromUid && user.email && msg.fromUid === user.email) ||
    // Check legacy fields
    (msg.senderId && user.uid && msg.senderId === user.uid) ||
    // Admin check
    (msg.isAdmin && user.email === 'ytproudltd@gmail.com')
  );
  
  if (!isFromCurrentUser) return null;

  // Handle optimistic UI status
  if (msg.status === 'sending') {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-400 animate-pulse">⏳</span>
      </div>
    );
  } else if (msg.status === 'failed') {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-red-500" title={msg.error || 'Failed to send'}>❌</span>
      </div>
    );
  } else if (msg.status === 'sent') {
    return (
      <div className="flex items-center gap-1">
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      </div>
    );
  } else if (msg.status === 'delivered') {
    return (
      <div className="flex items-center">
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg className="w-4 h-4 text-gray-400 -ml-3" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      </div>
    );
  } else if (msg.status === 'read') {
    return (
      <div className="flex items-center">
        <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'} -ml-3`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      </div>
    );
  } else if (msg.status === 'offline') {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-yellow-500">📤</span>
      </div>
    );
  }

  // For agent chat, show WhatsApp-style ticks
  if (conversation && conversation.id === 'admin') {
    const isReadByAdmin = msg.readByAdmin === true;
    return (
      <div className="flex items-center">
        {isReadByAdmin ? (
          // Double blue ticks for read messages (WhatsApp style)
          <>
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <svg className="w-4 h-4 text-blue-500 -ml-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </>
        ) : (
          // Single gray tick for sent messages (WhatsApp style)
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        )}
      </div>
    );
  }

  // Determine the other party's email
  let otherPartyEmail = null;
  if (conversation && conversation.buyer && conversation.seller && user) {
    if (conversation.buyer.email === user.email) {
      otherPartyEmail = conversation.seller.email;
    } else if (conversation.seller.email === user.email) {
      otherPartyEmail = conversation.buyer.email;
    }
  }
  const isRead = Array.isArray(msg.readBy) && otherPartyEmail && msg.readBy.includes(otherPartyEmail);

  // ⚡️ WHATSAPP STYLE: Show blue ticks for read, gray for sent
  return (
    <div className="flex items-center">
      {isRead ? (
        // Double blue ticks for read messages (WhatsApp style)
        <>
          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <svg className="w-4 h-4 text-blue-500 -ml-3" fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </>
      ) : (
        // Single gray tick for sent messages (WhatsApp style)
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      )}
    </div>
  );
};

export default MessageStatus;
